import { useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { ArrowRight, CircleSlash } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { companiesService, IndustryLinkReport, IndustryLinkRow } from '@/services/companies.service';
import { industryIcon } from '@/pages/industry-configs/industry-meta';
import { industryGroup, industryLabel } from './industry-link-meta';
import { LinkStateChip } from './CompanyIndustryCard';

function Industry({ value, caption }: { value: string | null; caption: string }) {
  const Icon = value ? industryIcon(value, industryGroup(value)) : CircleSlash;
  return (
    <div className="min-w-0 flex-1">
      <div className="text-2xs font-semibold uppercase tracking-[0.08em] text-text-muted">{caption}</div>
      <div className={clsx('mt-0.5 flex items-center gap-1.5 text-sm', value ? 'font-semibold text-text-primary' : 'text-text-muted')}>
        <Icon size={14} className={value ? 'text-primary' : undefined} />
        <span className="truncate">{industryLabel(value) ?? 'Nothing'}</span>
      </div>
    </div>
  );
}

/** Companies whose super-admin industry and CRM industry disagree, with one-click repair. */
export function IndustryLinkReview({ open, onClose, report, onResolved }: {
  open: boolean;
  onClose: () => void;
  report: IndustryLinkReport | null;
  onResolved: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const rows = (report?.items ?? []).filter((r) => r.state === 'mismatch' || r.state === 'crm_only');

  const resolve = async (row: IndustryLinkRow, keep: 'company' | 'profile') => {
    const target = keep === 'company' ? row.companyIndustry : row.profileIndustry;
    const msg = keep === 'company'
      ? `Switch ${row.name}'s CRM from ${industryLabel(row.profileIndustry)} to ${industryLabel(target)}? Their AI assistant, departments and packs follow.`
      : `Assign ${industryLabel(target)} to ${row.name}? It's what their CRM already uses; its default fields are copied in.`;
    if (!window.confirm(msg)) return;
    setBusy(row.companyUid + keep);
    try {
      await companiesService.resolveIndustry(row.companyUid, keep);
      toast.success(`${row.name}: industry linked`);
      onResolved();
    } catch { /* interceptor toasts */ } finally { setBusy(null); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Industry link review" widthClass="max-w-2xl">
      <p className="mb-4 text-sm text-text-muted">
        These companies were set up before the super-admin and CRM industries were linked. Pick the industry each one really is —
        the CRM's AI assistant, departments and packs follow your choice.
      </p>
      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border px-4 py-10 text-center text-sm text-text-muted">
          Every company is linked. Nothing to review.
        </div>
      ) : (
        <ul className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
          {rows.map((r) => (
            <li key={r.companyUid} className="rounded-xl border border-border bg-bg/40 p-3.5">
              <div className="mb-3 flex items-center gap-2.5">
                {r.logoUrl ? (
                  <img src={r.logoUrl} alt="" className="h-8 w-8 rounded-full border border-border object-cover" />
                ) : (
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-2xs font-bold text-primary">
                    {r.name.split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase()}
                  </span>
                )}
                <Link to={`/companies/${r.companyUid}`} onClick={onClose} className="min-w-0 flex-1 truncate text-sm font-semibold text-text-primary hover:text-primary">
                  {r.name}
                </Link>
                <LinkStateChip state={r.state} />
              </div>
              <div className="flex items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2.5">
                <Industry value={r.companyIndustry} caption="Assigned here" />
                <ArrowRight size={14} className="shrink-0 text-text-muted" />
                <Industry value={r.profileIndustry} caption="CRM runs as" />
              </div>
              <div className="mt-3 flex flex-wrap justify-end gap-2">
                {r.state === 'mismatch' && (
                  <Button size="sm" variant="secondary" loading={busy === r.companyUid + 'company'} disabled={!!busy} onClick={() => resolve(r, 'company')}>
                    Keep {industryLabel(r.companyIndustry)}
                  </Button>
                )}
                <Button size="sm" loading={busy === r.companyUid + 'profile'} disabled={!!busy} onClick={() => resolve(r, 'profile')}>
                  Use {industryLabel(r.profileIndustry)}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
